import { ComboBox, IconButton, IDropdownOption, ITooltipHostStyles, Stack, Toggle, TooltipHost } from '@fluentui/react';
import { ColumnDto, DataverseIntegration, IntegrationEventType } from '../../../../../../../api/generated/data-contracts';
import { Dispatch, SetStateAction, useRef } from 'react';
import { DATAVERSE_ASSIGNMENT_COLUMNS, DATAVERSE_ASSIGNMENTRESOURCE_COLUMNS, DESELECT_KEY } from '../../../../../../../constants/queryKeys';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../../api/api';
import { onRenderOption } from '../../../components/ComboBoxTooltip';
import { useId } from '@fluentui/react-hooks';
import { IActualsCanBeEnabled } from '../DataverseLayout';
import { GetHostName } from '../../../../../../../helpers/trimUrl';

interface IAssignmentPaneProps {
    integration: DataverseIntegration;
    dataverseTables: IDropdownOption<any>[];
    setIntegration: Dispatch<SetStateAction<DataverseIntegration>>;
    toggleEvent: (event: IntegrationEventType) => void;
    canBeEnabled: IActualsCanBeEnabled;
}

export const AssignmentPane = (props: IAssignmentPaneProps) => {
    const {
        dataverseUrl,
        assignmentTableLogicalName,
        assignmentResourceTableLogicalName,
        assignmentIdColumn,
        assignmentResourceIdColumn,
        assignmentPlannedWorkColumn,
        assignmentRemainingWorkColumn,
        assignmentResourceAzureIdColumn,
        assignmentRateColumn,
        assignmentTaskIdColumn,
        actualColumn,
        assignmentWorkTypeColumn,
        assignmentClosedColumn,
        assignmentPinnedColumn,
    } = props.integration.configuration;

    const defaultColumns = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: assignmentOptions = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_ASSIGNMENT_COLUMNS, dataverseUrl, assignmentTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: assignmentTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && assignmentTableLogicalName),
        suspense: true,
    });

    const { data: resourceIdOptions = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_ASSIGNMENTRESOURCE_COLUMNS, dataverseUrl, assignmentResourceTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: assignmentResourceTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && assignmentResourceTableLogicalName),
        suspense: true,
    });

    const tooltipHostStyles: Partial<ITooltipHostStyles> = {
        root: { display: 'inline-block', marginLeft: 4 }, // Adjust the margin as needed
    };

    const calloutProps = { gapSpace: 0 };
    const tooltipId = useId('tooltip');

    const iconWithTooltip = (
        <TooltipHost
            id={tooltipId}
            calloutProps={calloutProps}
            styles={tooltipHostStyles}
            content={`${props.canBeEnabled.canBeEnabled ? 'This is for syncing actuals from Time for Teams into Dataverse.' : props.canBeEnabled.message}`}
        >
            <IconButton iconProps={{ iconName: 'Info' }} aria-describedby={tooltipId} ariaLabel="Info" title="Info" />
        </TooltipHost>
    );

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            <ComboBox
                label="Assignment Table"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={assignmentTableLogicalName ? '' : 'Table required'}
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    if (opt) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.assignmentTableLogicalName = opt.key as string;
                                integration.configuration.assignmentTableLogicalCollectionName = opt.data.logicalCollectionName;
                            }),
                        );
                    } else if (!value) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.assignmentTableLogicalName = '';
                                integration.configuration.assignmentTableLogicalCollectionName = '';
                            }),
                        );
                    }
                }}
                selectedKey={assignmentTableLogicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Id"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={assignmentIdColumn ? '' : 'Assignment Id column required'}
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentIdColumn}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Task Id"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={assignmentTaskIdColumn ? '' : 'Task Id column required'}
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentTaskIdColumn = (opt?.key as string) || value || '';
                            integration.configuration.assignmentTaskSchemaColumn = opt?.data?.schemaName || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentTaskIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Resource Id"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={assignmentResourceIdColumn ? '' : 'Assignment Resource Id column required'}
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentResourceIdColumn = (opt?.key as string) || value || '';
                            integration.configuration.assignmentResourceSchemaColumn = opt?.data?.schemaName;
                            integration.configuration.assignmentResourceTableReferencedName = opt?.data?.logicalCollectionName || '';
                            integration.configuration.assignmentResourceReferencedKeyAttribute = opt?.data?.referencedKeyAttribute || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentResourceIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Planned Work"
                allowFreeform
                autoComplete="on"
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentPlannedWorkColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentPlannedWorkColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Remaining Work"
                allowFreeform
                autoComplete="on"
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentRemainingWorkColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentRemainingWorkColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Work Type"
                allowFreeform
                autoComplete="on"
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentWorkTypeColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentWorkTypeColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Rate"
                allowFreeform
                autoComplete="on"
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentRateColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentRateColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Closed"
                allowFreeform
                autoComplete="on"
                options={assignmentOptions.filter(o => o.data.attributeType === 'Boolean')}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentClosedColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentClosedColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Assignment Pinned"
                allowFreeform
                autoComplete="on"
                options={assignmentOptions.filter(o => o.data.attributeType === 'Boolean')}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentPinnedColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={assignmentPinnedColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Resource Azure Id Alternate Table"
                allowFreeform
                autoComplete="on"
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentResourceTableLogicalName = (opt?.data?.logicalName as string) || value || '';
                            integration.configuration.assignmentResourceTableLogicalCollectionName = opt?.data?.logicalCollectionName || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                // This is done so we can avoid having both the logicalName and logicalCollectionName in the configuration
                selectedKey={props.dataverseTables.find(t => t.key === assignmentResourceTableLogicalName)?.data?.logicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Resource Azure Id Alternate Column"
                allowFreeform
                autoComplete="on"
                options={resourceIdOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.assignmentResourceAzureIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceIdOptions.length <= 0}
                selectedKey={assignmentResourceAzureIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <Toggle
                label={<div>Use Actuals {iconWithTooltip} </div>}
                inlineLabel
                onChange={() => props.toggleEvent('Actuals')}
                disabled={!props.canBeEnabled.canBeEnabled}
                checked={props.integration?.subscribedEvents?.includes('Actuals')}
                styles={{ root: { margin: '5, 10, 5, 0' }, pill: { marginTop: 7 }, label: { marginLeft: 5 } }}
            />
            <ComboBox
                label="Actuals Column"
                allowFreeform
                autoComplete="on"
                options={assignmentOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.actualColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={assignmentOptions.length <= 0}
                selectedKey={actualColumn ?? ''}
                onRenderOption={onRenderOption}
            />
        </Stack>
    );
};
